import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { 
  Users, 
  Building2, 
  Activity, 
  DollarSign,
  Zap,
  BarChart3,
  TrendingUp
} from 'lucide-react';

interface MetricWidgetProps {
  id: string;
  color: string;
  metrics: SystemMetrics | null;
}

interface SystemMetrics {
  users: {
    total: number;
    free: number;
    premium: number;
  };
  accounts: {
    total: number;
    free: number;
    premium: number;
  };
  dailyActiveUsers: {
    average: number;
    percentage: string;
  };
  integrations: {
    xero: number;
    gmail: number;
    outlook: number;
    sms: number;
    total: number;
  };
  revenue: {
    monthlyRevenue: number;
    currency: string;
  };
}

export const MetricWidget: React.FC<MetricWidgetProps> = ({ id, color, metrics }) => {
  const { t } = useTranslation();

  if (!metrics) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const renderMetric = () => {
    switch (id) {
      case 'metric-total-users':
        return (
          <>
            <div className={`${color} p-3 rounded-lg text-white`}>
              <Users className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {metrics.users.total.toLocaleString()}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('dashboard.metricTotalUsersLabel')}</p>
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                {metrics.users.free} {t('dashboard.metricFree')} / {metrics.users.premium} {t('dashboard.metricPremium')}
              </div>
            </div>
          </>
        );

      case 'metric-total-accounts':
        return (
          <>
            <div className={`${color} p-3 rounded-lg text-white`}>
              <Building2 className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {metrics.accounts.total.toLocaleString()}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('dashboard.metricTotalAccountsLabel')}</p>
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                {metrics.accounts.free} {t('dashboard.metricFree')} / {metrics.accounts.premium} {t('dashboard.metricPremium')}
              </div>
            </div>
          </>
        );

      case 'metric-daily-active-users':
        return (
          <>
            <div className={`${color} p-3 rounded-lg text-white`}>
              <Activity className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {metrics.dailyActiveUsers.average.toLocaleString()}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('dashboard.metricAvgDailyActiveUsers')}</p>
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                {metrics.dailyActiveUsers.percentage}{t('dashboard.metricOfTotalUsers')}
              </div>
            </div>
          </>
        );

      case 'metric-monthly-revenue':
        return (
          <>
            <div className={`${color} p-3 rounded-lg text-white`}>
              <DollarSign className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                ${metrics.revenue.monthlyRevenue.toLocaleString('en-US', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('dashboard.metricMonthlyRevenueLabel')}</p>
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                {metrics.revenue.currency}
              </div>
            </div>
          </>
        );

      case 'metric-integrations':
        return (
          <>
            <div className={`${color} p-3 rounded-lg text-white`}>
              <Zap className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {metrics.integrations.total.toLocaleString()}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('dashboard.metricActiveIntegrationsLabel')}</p>
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-500 space-y-1">
                <div>{t('dashboard.metricXero')} {metrics.integrations.xero}</div>
                <div>{t('dashboard.metricEmail')} {metrics.integrations.gmail + metrics.integrations.outlook}, {t('dashboard.metricSMS')} {metrics.integrations.sms}</div>
              </div>
            </div>
          </>
        );

      case 'metric-detailed-metrics':
        return (
          <>
            <div className={`${color} p-3 rounded-lg text-white`}>
              <BarChart3 className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {t('dashboard.metricViewAllMetrics')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t('dashboard.metricDetailedCharts')}
              </p>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex items-start gap-4 h-full w-full">
      {renderMetric()}
    </div>
  );
};

